import { useRef, useState, type ReactNode } from 'react';

import { Box } from '@mui/material';

/** The bar's kind: a change of kind is what slides one bar out and the next in. */
export type FooterKind = 'none' | 'fields' | 'composer';

export interface FooterTransitionProps {
  kind: FooterKind;
  children: ReactNode;
}

const SLIDE_OUT = 'transform .22s ease';
const SLIDE_IN = 'footerIn .28s cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * FooterTransition drops the bar that is going down out of view, then brings
 * the one arriving up. A bar of the same kind swaps in place.
 */
export const FooterTransition = ({ kind, children }: FooterTransitionProps) => {
  const [shownKind, setShownKind] = useState(kind);
  // The bar on its way out keeps the content it left with.
  const leaving = useRef(children);
  const isIn = kind === shownKind;
  if (isIn) leaving.current = children;

  const settle = () => {
    if (!isIn) setShownKind(kind);
  };

  if (shownKind === 'none' && !isIn) {
    // Nothing to drop away, so the arriving bar comes straight up.
    setShownKind(kind);
    return null;
  }

  return (
    <Box
      sx={{
        flexShrink: 0,
        transform: isIn ? 'none' : 'translateY(100%)',
        transition: SLIDE_OUT,
      }}
      onTransitionEnd={settle}
    >
      {/* Keyed by kind so an arriving bar mounts, which is what plays the slide. */}
      <Box
        key={shownKind}
        sx={{
          '@keyframes footerIn': {
            from: { transform: 'translateY(100%)' },
            to: { transform: 'translateY(0)' },
          },
          animation: SLIDE_IN,
        }}
      >
        {isIn ? children : leaving.current}
      </Box>
    </Box>
  );
};

FooterTransition.displayName = 'FooterTransition';
export default FooterTransition;
